import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SingleInput from "@/components/controls/SingleInput";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useUsers } from "@/entities/users/hooks";
import { formatFullDate } from "@/utils/datetime";
import { ContractData, ContractTableStatus } from "@/pages/contracts/types";
import {
  formatFxRate,
  formatFxRateForEditing,
  parseFxRateInput
} from "@/pages/contracts/fxRate";
import { ContractFieldUpdate } from "./ContractDetailedView";

const TRANSLATION_KEY = "contracts.fxRate";

interface CloseFxRateFieldProps {
  rate: number | null | undefined;
  contractCurrency: string;
  accountCurrency: string;
}

// The rate on the day the contract closed. Fixed by the server, so it is only
// ever read here.
export const ContractCloseFxRateField: React.FC<CloseFxRateFieldProps> = ({
  rate,
  contractCurrency,
  accountCurrency
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-0.5 px-2"
      data-test="contract-close-fx-rate"
    >
      <Text variant="body">{t("contracts.closeFxRate")}</Text>
      <Text variant="body" className="px-2">
        {rate != null
          ? formatFxRate(rate, contractCurrency, accountCurrency)
          : "—"}
      </Text>
    </div>
  );
};

interface ManualRateMarkerProps {
  modifiedBy: string;
  modifiedAt: string | null | undefined;
}

// Says the rate was typed in by someone, and on hover who and when. The user
// is only looked up while the tooltip is open, so a header that nobody hovers
// costs no request.
const ManualRateMarker: React.FC<ManualRateMarkerProps> = ({
  modifiedBy,
  modifiedAt
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const userIds = useMemo(() => [modifiedBy], [modifiedBy]);
  const { users } = useUsers(userIds, { enabled: isOpen });
  // useUsers stands in an "Unknown" user until the real one loads; say
  // nothing about who rather than name that placeholder.
  const user = users[0];
  const userName =
    user && user.id === modifiedBy ? user.displayName : undefined;
  const date = modifiedAt ? formatFullDate(modifiedAt) : undefined;

  const tooltipText =
    userName && date
      ? t(`${TRANSLATION_KEY}.manualSetByOn`, { name: userName, date })
      : userName
        ? t(`${TRANSLATION_KEY}.manualSetBy`, { name: userName })
        : date
          ? t(`${TRANSLATION_KEY}.manualSetOn`, { date })
          : t(`${TRANSLATION_KEY}.manualTooltip`);

  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger asChild>
        <span tabIndex={0} data-test="contract-current-fx-rate-manual">
          <Badge
            text={t(`${TRANSLATION_KEY}.manual`)}
            textVariant="smBody"
            className="py-0.5"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent
        enableMarkdown={false}
        data-test="contract-current-fx-rate-manual-tooltip"
      >
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
};

interface CurrentFxRateFieldProps {
  contract: ContractData;
  contractCurrency: string;
  accountCurrency: string;
  canEdit: boolean;
  onUpdateField: ContractFieldUpdate;
}

// The rate the account-currency figures are priced at today. Refreshed by the
// server on the account's schedule unless someone sets it by hand, which pins
// it until they hand it back with "Use automatic rate".
export const ContractCurrentFxRateField: React.FC<CurrentFxRateFieldProps> = ({
  contract,
  contractCurrency,
  accountCurrency,
  canEdit,
  onUpdateField
}) => {
  const { t } = useTranslation();
  const rate = contract.currentFxRate ?? null;
  const modifiedBy = contract.currentFxRateModifiedBy ?? null;
  const refreshedAt = contract.currentFxRateRefreshedAt ?? null;
  // The server never returns a manual rate without a value, so a marker with
  // no rate is "Use automatic rate" still in flight: the edit cleared the rate
  // optimistically and the live one arrives with the response.
  const isSwitchingToAutomatic = modifiedBy != null && rate == null;
  const isManual = modifiedBy != null && !isSwitchingToAutomatic;
  // An expired contract stops refreshing, so its rate is the one it ended on.
  // Editing it would re-price a contract that no longer runs.
  const isExpired = contract.status === ContractTableStatus.EXPIRED;
  const disabled = !canEdit || isExpired;
  const disabledTooltipText = isExpired
    ? t(`${TRANSLATION_KEY}.frozen`, {
        date: contract.endDate ? formatFullDate(contract.endDate) : ""
      })
    : t("common.noEditPermission");

  const handleSave = (value: string) => {
    if (disabled) return;
    // The validation below already refuses these, so this only guards a save
    // that slips past it. Blank must not reach coerceContractFieldValue: it
    // turns "" into null, which would silently switch the rate to automatic.
    const parsed = parseFxRateInput(value);
    if (parsed === null) return;
    onUpdateField("currentFxRate", parsed);
  };

  const input = (
    <div
      className="flex items-center"
      data-test="contract-current-fx-rate-editor"
    >
      <SingleInput
        key={`currentFxRate-${contract.id}-${rate}-${modifiedBy}`}
        fieldName="currentFxRate"
        initialValue={rate != null ? formatFxRateForEditing(rate) : ""}
        onSave={handleSave}
        validation={{
          customRules: [
            {
              validate: (value: string) => parseFxRateInput(value) !== null,
              message: t(`${TRANSLATION_KEY}.invalidRate`)
            }
          ]
        }}
        textVariant="body"
        width="auto"
        disabled={disabled}
        customEditButton={
          <Text
            variant="body"
            className="px-2"
            data-test="contract-current-fx-rate-value"
          >
            {rate != null
              ? formatFxRate(rate, contractCurrency, accountCurrency)
              : "—"}
          </Text>
        }
      />
    </div>
  );

  return (
    <div
      className="flex items-center gap-0.5 px-2"
      data-test="contract-current-fx-rate"
    >
      <Text variant="body">{t("contracts.currentFxRate")}</Text>
      {disabled ? (
        <Tooltip>
          <TooltipTrigger asChild>{input}</TooltipTrigger>
          <TooltipContent
            enableMarkdown={false}
            data-test="contract-current-fx-rate-disabled-tooltip"
          >
            {disabledTooltipText}
          </TooltipContent>
        </Tooltip>
      ) : (
        input
      )}
      {isSwitchingToAutomatic ? (
        <Text
          variant="bodySecondary"
          className="px-1"
          data-test="contract-current-fx-rate-switching"
        >
          {t(`${TRANSLATION_KEY}.switchingToAutomatic`)}
        </Text>
      ) : isManual ? (
        <>
          <ManualRateMarker modifiedBy={modifiedBy} modifiedAt={refreshedAt} />
          {!disabled && (
            <Button
              variant="tertiary"
              onClick={() => onUpdateField("currentFxRate", null)}
              data-test="contract-current-fx-rate-use-automatic"
            >
              {t(`${TRANSLATION_KEY}.useAutomatic`)}
            </Button>
          )}
        </>
      ) : (
        refreshedAt && (
          <Text
            variant="bodySecondary"
            className="px-1"
            data-test="contract-current-fx-rate-updated"
          >
            {t(`${TRANSLATION_KEY}.updatedOn`, {
              date: formatFullDate(refreshedAt)
            })}
          </Text>
        )
      )}
    </div>
  );
};
